/**
 * components/KillSwitch — the always-visible emergency control (PLAN §5: the one
 * button that must never be blocked). Lives in the top bar. Clicking opens a
 * confirm-guarded dialog; confirming POSTs `/api/killswitch` with the required
 * `confirm: "HALT"` token (dry-run in demo), which cancel-alls + flattens + halts.
 * On success it shows the dry-run outcome inline so the operator sees exactly what
 * the halt did. It never depends on any panel's data.
 */
import { useState } from 'react';

import { api } from '../api/client';
import type { KillOutcome } from '../api/types';

interface KillSwitchProps {
  /** Current halt state, so the trigger can show ARMED vs HALTED. */
  readonly halted: boolean;
}

export function KillSwitch({ halted }: KillSwitchProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<KillOutcome | null>(null);

  const close = (): void => {
    setOpen(false);
    setReason('');
    setError(null);
    setOutcome(null);
  };

  const confirm = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.kill(reason.trim() || 'dashboard kill switch');
      setOutcome(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kill request failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        className={`kill${halted ? ' kill--halted' : ''}`}
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        title="Emergency halt: cancel all orders, flatten positions, block new entries"
      >
        <span className="kill__dot" aria-hidden />
        <span className="kill__label">{halted ? 'Halted' : 'Kill'}</span>
      </button>

      {open && (
        <div
          className="kill-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="kill-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !busy) close();
          }}
        >
          <div className="kill-dialog">
            {outcome ? (
              <>
                <h2 id="kill-title">Halt engaged</h2>
                <p>
                  The book is flat and new entries are blocked until re-armed.
                  {outcome.dryRun && <span className="secondary"> (demo dry-run — no live orders placed)</span>}
                </p>
                <dl className="kill-outcome">
                  <div>
                    <dt>Flattened</dt>
                    <dd className="num">{outcome.flattened.length ? outcome.flattened.join(', ') : 'none'}</dd>
                  </div>
                  <div>
                    <dt>Orders cancelled</dt>
                    <dd className="num">{outcome.cancelled.length}</dd>
                  </div>
                  <div>
                    <dt>Errors</dt>
                    <dd className="num">{outcome.errors.length ? outcome.errors.join('; ') : 'none'}</dd>
                  </div>
                </dl>
                <div className="kill-dialog__actions">
                  <button className="btn btn--primary" type="button" onClick={close} style={{ flex: 1 }}>
                    Done
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 id="kill-title">Confirm emergency halt</h2>
                <p>
                  This cancels all open orders, flattens every position, and blocks new entries
                  until re-armed. This is a demo (dry-run) environment.
                </p>

                <div className="field">
                  <label htmlFor="kill-reason">Reason (optional)</label>
                  <input
                    id="kill-reason"
                    className="input"
                    type="text"
                    placeholder="e.g. macro shock, manual override"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    autoFocus
                  />
                </div>

                {error && (
                  <div className="banner-error" role="alert">
                    {error}
                  </div>
                )}

                <div className="kill-dialog__actions">
                  <button className="btn btn--ghost" type="button" onClick={close} disabled={busy}>
                    Cancel
                  </button>
                  <button className="btn btn--danger" type="button" onClick={confirm} disabled={busy}>
                    {busy ? 'Halting…' : 'Confirm kill'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
